import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, ArrowRight, RefreshCw, Shield } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { QuestionnaireSessionService } from '../services/questionnaireSession';
import { getRecommendations } from '../utils/recommendations';
import { logger } from '../utils/logger';
import { LoadingState } from '../components/recommendations/LoadingState';
import Logo from '../components/Logo';
import type { Recommendation, QuestionnaireData } from '../types';

const Recommendations = lazy(() => import('../components/Recommendations'));

export function SuccessPage() {
  const [, setLocation] = useLocation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [paymentNotVerified, setPaymentNotVerified] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const loadSessionData = useCallback(async (useRetry: boolean = false) => {
    try {
      setIsLoading(true);
      setHasError(false);
      setPaymentNotVerified(false);

      const urlParams = new URLSearchParams(window.location.search);
      const sessionIdParam = urlParams.get('session_id');
      setSessionId(sessionIdParam);

      if (!sessionIdParam) {
        setLocation('/unlock');
        return;
      }

      const session = useRetry
        ? await QuestionnaireSessionService.getSessionWithRetry(sessionIdParam)
        : await QuestionnaireSessionService.getSession(sessionIdParam);

      if (!session || !session.form_data) {
        logger.warn('No session data found after payment');
        setHasError(true);
        setIsLoading(false);
        return;
      }

      if (!session.completed) {
        setIsVerifying(true);
        const verified = await QuestionnaireSessionService.waitForPaymentVerification(sessionIdParam);
        setIsVerifying(false);

        if (!verified) {
          setPaymentNotVerified(true);
          setIsLoading(false);
          return;
        }
      }

      const formData = session.form_data as QuestionnaireData;
      setQuestionnaireData(formData);
      setUserName(session.name || (formData as any).name || '');
      setUserEmail(session.email || (formData as any).email || '');

      const recs = getRecommendations(formData);
      if (recs.length === 0) {
        logger.error('No recommendations generated from form data', { formData });
        setHasError(true);
        setIsLoading(false);
        return;
      }

      setRecommendations(recs);
      setIsLoading(false);
    } catch (error) {
      logger.error('Error loading session data on success page', error);
      setHasError(true);
      setIsLoading(false);
    }
  }, [setLocation]);

  useEffect(() => {
    loadSessionData(true);
  }, [loadSessionData]);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    await loadSessionData(true);
    setIsRetrying(false);
  }, [loadSessionData]);

  const handleStartOver = () => {
    window.location.href = '/questionnaire';
  };

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Loading Your Results - btoolme</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col items-center justify-center">
          <div className="text-center mb-8">
            {isVerifying ? (
              <>
                <Shield className="mx-auto h-16 w-16 text-blue-500 mb-4 animate-pulse" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment...</h1>
                <p className="text-gray-600">Confirming your payment with our processor. This usually takes just a few seconds.</p>
              </>
            ) : (
              <>
                <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
                <p className="text-gray-600">Loading your personalized recommendations...</p>
              </>
            )}
          </div>
          <LoadingState />
        </div>
      </>
    );
  }

  if (paymentNotVerified) {
    return (
      <>
        <Helmet>
          <title>Payment Verification - btoolme</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-lg mx-auto px-4 py-12 sm:py-16 text-center">
            <Logo className="mx-auto mb-8" />
            <Shield className="mx-auto h-16 w-16 text-amber-500 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Payment Not Yet Confirmed</h1>
            <p className="text-gray-600 mb-6">
              We haven't received confirmation of your payment yet. If you just completed checkout,
              please wait a moment and try again. If you haven't paid yet, return to unlock your results.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${isRetrying ? 'animate-spin' : ''}`} />
                {isRetrying ? 'Checking...' : 'Check Again'}
              </button>
              <Link
                href="/unlock"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
              >
                Return to Unlock Page
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            {sessionId && (
              <p className="mt-6 text-xs text-gray-500">
                Reference: {sessionId}
              </p>
            )}
          </div>
        </div>
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <Helmet>
          <title>Error Loading Results - btoolme</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-lg mx-auto px-4 py-12 sm:py-16 text-center">
            <Logo className="mx-auto mb-8" />
            <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Payment Successful!</h1>
            <p className="text-gray-600 mb-6">
              Your payment was processed successfully, but we're having trouble loading your recommendations.
              This can happen if you cleared your browser data or are using a different device.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${isRetrying ? 'animate-spin' : ''}`} />
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </button>
              <Link
                href="/questionnaire"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
              >
                Start New Questionnaire
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            {sessionId && (
              <p className="mt-6 text-xs text-gray-500">
                Reference: {sessionId}
              </p>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Your Business Software Blueprint - btoolme</title>
        <meta name="description" content="Your personalized software recommendations are ready." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="bg-green-50 border-b border-green-100">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="text-green-800 font-medium">Payment successful! Here are your personalized recommendations.</p>
          </div>
        </div>

        <div className="container mx-auto py-8">
          <Suspense fallback={<LoadingState />}>
            <Recommendations
              recommendations={recommendations}
              userEmail={userEmail}
              userName={userName}
              questionnaireData={questionnaireData}
              onStartOver={handleStartOver}
            />
          </Suspense>
        </div>
      </div>
    </>
  );
}
